import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import { ClinicOfficeHourSelectionFragment, ClinicOfficeHoursType } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useMutation } from '@apollo/client';
import { CREATE_OFFICE_HOURS } from 'shared/mutations';
import { STATUSES } from 'shared/hooks/useCustomToast';
import isEqual from 'lodash-es/isEqual';
import { Text } from '@televet/kibble-ui/build/components/Text';
import cloneDeep from 'lodash-es/cloneDeep';
import { toSentenceList } from 'shared/utils';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsClincHoursEvents } from 'shared/providers/TrackingProvider/EventNames';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { TimeRange } from '@televet/kibble-ui/build/components/TimeRange';
import { convertToUTC } from 'shared/utils/formatDate';

interface IOfficeHourPicker {
  hasPermission?: boolean;
  clinicHoursData: ClinicOfficeHourSelectionFragment[] | undefined;
  clinicTimezone: string;
  clinicOfficeHoursType: ClinicOfficeHoursType;
  toastMessagePop: (description?: string, status?: STATUSES) => void;
  setClinicHoursChanges: (value: boolean, newOfficeHours?: ClinicOfficeHourSelectionFragment[]) => void;
}

export type CanceledButtonClicked = {
  onResetOfficeHours: () => void;
};

enum IClinicHourSectionField {
  OpenAt = 'openAt',
  CloseAt = 'closeAt',
  IsEnabled = 'isEnabled',
  IsOpenTwentyFourHours = 'isOpenTwentyFourHours',
}

const startDate = new Date();
startDate.setHours(8, 0, 0, 0);
const endDate = new Date();
endDate.setHours(17, 0, 0, 0);

export const DaysOfWeek: Record<string, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const OfficeHoursPicker = forwardRef(
  (
    { hasPermission, clinicOfficeHoursType, clinicHoursData, clinicTimezone, setClinicHoursChanges }: IOfficeHourPicker,
    ref,
  ): JSX.Element => {
    const [daysOfWeekWInvalidHr, setDaysOfWeekWInvalidHr] = useState<number[]>([]);
    const [showCreateButton, setShowCreateButton] = useState<boolean>(false);
    const [officeHoursState, setOfficeHoursState] = useState<ClinicOfficeHourSelectionFragment[]>([]);
    const clinicHours: ClinicOfficeHourSelectionFragment[] = useMemo(() => [], []);
    const { trackEvent } = useTracking();

    const { currentClinicId } = useClinicUser();

    const officeHourTerminology = useMemo(() => {
      switch (clinicOfficeHoursType) {
        case ClinicOfficeHoursType.BusinessHours:
          return { ot: 'Open', ct: 'Closed', tt: 'Business Hours' };
        case ClinicOfficeHoursType.AutomationHours:
          return { ot: 'Send', ct: 'Do not send', tt: 'Automation Hours' };
        default:
          return { ot: 'Open', ct: 'Closed', tt: 'Business Hours' };
      }
    }, [clinicOfficeHoursType]);

    const officeHoursCreateArray = useMemo(() => {
      const newHoursArray = [];
      const officeHoursArray = clinicHoursData?.filter(
        (officeHour) => officeHour.type === ClinicOfficeHoursType.BusinessHours,
      );
      if (officeHoursArray && officeHoursArray.length > 0) {
        for (const officeHour of officeHoursArray) {
          const { dayOfWeek, openAt, closeAt, isEnabled, isOpenTwentyFourHours } = officeHour;
          const obj = { dayOfWeek, openAt, closeAt, isEnabled, isOpenTwentyFourHours, type: clinicOfficeHoursType };

          newHoursArray.push(obj);
        }
      } else {
        for (let i = 0; i < 7; i++) {
          newHoursArray.push({
            dayOfWeek: i,
            openAt: startDate.toISOString(),
            closeAt: endDate.toISOString(),
            isEnabled: false,
            isOpenTwentyFourHours: false,
            type: clinicOfficeHoursType,
          });
        }
      }
      return newHoursArray;
    }, [clinicOfficeHoursType, clinicHoursData]);

    const [createOfficeHours, { loading: creatingOfficeHours }] = useMutation(CREATE_OFFICE_HOURS);

    /**
     * Move office hours state to local state for a snappier
     * user experience
     */
    const onSetInitialData = useCallback(
      (onCancelChange?: boolean, passedClinicHourData?: ClinicOfficeHourSelectionFragment[]) => {
        if (clinicHoursData && currentClinicId) {
          const officeHours = (passedClinicHourData || clinicHoursData)
            .filter((officeHour) => officeHour.type === clinicOfficeHoursType)
            .sort((a, b) => a.dayOfWeek - b.dayOfWeek);

          if (officeHours.length) {
            const officeHoursCopy = [...officeHours];

            officeHoursCopy.push(officeHoursCopy[0]);
            officeHoursCopy.shift();

            if (!isEqual(officeHoursCopy, clinicHours) || !!onCancelChange) {
              setOfficeHoursState(officeHoursCopy);
              setShowCreateButton(false);
              Object.assign(clinicHours, officeHoursCopy);
            }
          } else {
            setOfficeHoursState([]);
            setShowCreateButton(true);
          }
        }
      },
      [clinicHoursData, currentClinicId, clinicOfficeHoursType, clinicHours],
    );

    useEffect(() => {
      onSetInitialData();
    }, [onSetInitialData]);

    const handleRemoveDaysOfWeekWInvalidHr = useCallback(
      (updatedOfficeHourDayOfWeek: number, daysOfWeekWInvalidHrReplica: number[]) => {
        const index = daysOfWeekWInvalidHrReplica.indexOf(updatedOfficeHourDayOfWeek);
        daysOfWeekWInvalidHrReplica.splice(index, 1);
        setDaysOfWeekWInvalidHr(daysOfWeekWInvalidHrReplica);
      },
      [],
    );

    const handleSetDaysOfWeekWInvalidHr = useCallback(
      (updatedOfficeHourDayOfWeek: number, daysOfWeekWInvalidHrReplica: number[]) => {
        setDaysOfWeekWInvalidHr((prevVal) => [...prevVal, updatedOfficeHourDayOfWeek]);
        daysOfWeekWInvalidHrReplica.push(updatedOfficeHourDayOfWeek);
      },
      [],
    );

    const onClinicHourSectionChange = useCallback(
      (index: number, field: IClinicHourSectionField, value: Date | boolean): void => {
        if (!currentClinicId && !officeHoursState) return;

        const officeHoursCopy = cloneDeep(officeHoursState);
        const updatedOfficeHour = {
          ...officeHoursCopy[index],
          [field]: value,
        };

        const today = new Date();
        const zonedOpenAt = utcToZonedTime(updatedOfficeHour.openAt, clinicTimezone);
        const zonedCloseAt = utcToZonedTime(updatedOfficeHour.closeAt, clinicTimezone);

        // Set both times to the same date to compare only time components
        zonedOpenAt.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
        zonedCloseAt.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());

        const openTimeValue = zonedOpenAt.valueOf();
        const closeTimeValue = zonedCloseAt.valueOf();

        const daysOfWeekWInvalidHrReplica: number[] = [...daysOfWeekWInvalidHr];
        const updatedOfficeHourDayOfWeek = updatedOfficeHour.dayOfWeek;

        if (field === IClinicHourSectionField.IsEnabled) {
          if (!value) {
            updatedOfficeHour.openAt = clinicHours[index].openAt;
            updatedOfficeHour.closeAt = clinicHours[index].closeAt;
            updatedOfficeHour.isOpenTwentyFourHours = clinicHours[index].isOpenTwentyFourHours;
            daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek) &&
              handleRemoveDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
          } else if (
            value &&
            updatedOfficeHour.isOpenTwentyFourHours &&
            daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)
          ) {
            handleRemoveDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
          } else if (value && !updatedOfficeHour.isOpenTwentyFourHours) {
            if (openTimeValue >= closeTimeValue && !daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)) {
              handleSetDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
            }
          }
        } else if (field === IClinicHourSectionField.IsOpenTwentyFourHours) {
          if (value && daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)) {
            handleRemoveDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
            updatedOfficeHour.openAt = clinicHours[index].openAt;
            updatedOfficeHour.closeAt = clinicHours[index].closeAt;
          } else if (!value) {
            if (openTimeValue >= closeTimeValue && !daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)) {
              handleSetDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
            }
          }
        } else if (openTimeValue >= closeTimeValue && !daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)) {
          handleSetDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
        } else if (openTimeValue < closeTimeValue && daysOfWeekWInvalidHr.includes(updatedOfficeHourDayOfWeek)) {
          handleRemoveDaysOfWeekWInvalidHr(updatedOfficeHourDayOfWeek, daysOfWeekWInvalidHrReplica);
        }

        value instanceof Date &&
          trackEvent(SettingsClincHoursEvents.EditOfficeHours, {
            dayOfWeek: DaysOfWeek[updatedOfficeHourDayOfWeek],
            [field]: format(value, 'h:mm a'),
          });

        officeHoursCopy[index] = updatedOfficeHour;
        setOfficeHoursState(officeHoursCopy);

        if (isEqual(officeHoursCopy, clinicHours) || daysOfWeekWInvalidHrReplica.length > 0) {
          setClinicHoursChanges(false);
        } else {
          setClinicHoursChanges(true, officeHoursCopy);
        }
      },
      [
        currentClinicId,
        officeHoursState,
        clinicTimezone,
        daysOfWeekWInvalidHr,
        trackEvent,
        clinicHours,
        handleRemoveDaysOfWeekWInvalidHr,
        handleSetDaysOfWeekWInvalidHr,
        setClinicHoursChanges,
      ],
    );

    const onCreateOfficeHour = async (): Promise<void> => {
      const resp = await createOfficeHours({
        variables: {
          id: currentClinicId,
          officeHours: officeHoursCreateArray,
        },
      });
      setShowCreateButton(false);
      const officeHours: ClinicOfficeHourSelectionFragment[] = resp.data.updateOneClinic.clinicOfficeHours;
      if (officeHours) {
        onSetInitialData(false, officeHours);
      }
    };

    useImperativeHandle(ref, () => ({
      onResetOfficeHours(): void {
        onSetInitialData(true);
        setClinicHoursChanges(false);
      },
    }));

    const handleTimeChange = useCallback(
      ({ index, field, date }: { index: number; field: IClinicHourSectionField; date: Date }): void => {
        const _date = convertToUTC({ date, timeZone: clinicTimezone });
        onClinicHourSectionChange(index, field, _date);
      },
      [clinicTimezone, onClinicHourSectionChange],
    );

    return (
      <Flex flexDirection="column" justifyContent="center" alignItems="center" w="650px">
        {showCreateButton && !creatingOfficeHours && (
          <Button variant="secondary" iconName="plus" onClick={(): Promise<void> => onCreateOfficeHour()}>
            {`Create ${officeHourTerminology.tt}`}
          </Button>
        )}
        {daysOfWeekWInvalidHr.length > 0 && (
          <Text as="p" size="xs" variant="danger" mb={5}>{`Your Opening hours for ${toSentenceList(
            daysOfWeekWInvalidHr.sort().map((err) => DaysOfWeek[err]),
          )} must be earlier than Closing hours.`}</Text>
        )}
        {officeHoursState.map((officeHour: ClinicOfficeHourSelectionFragment, index: number) => {
          return (
            <Flex
              key={officeHour.id}
              alignItems="center"
              justifyContent="space-between"
              py={1}
              px={2}
              w="100%"
              minH="35px"
            >
              <Box fontSize="sm" flex={1} maxWidth="150px" zIndex={0}>
                {hasPermission ? (
                  <Switch
                    name={`isEnabled-${index}`}
                    label={DaysOfWeek[officeHour.dayOfWeek]}
                    isChecked={officeHour.isEnabled}
                    onChange={(e): void =>
                      onClinicHourSectionChange(index, IClinicHourSectionField.IsEnabled, e.currentTarget.checked)
                    }
                    isFieldInline
                    labelPosition="end"
                    labelStyle={{ fontSize: 'sm' }}
                  />
                ) : (
                  <Text size="sm">{DaysOfWeek[officeHour.dayOfWeek]}</Text>
                )}
              </Box>
              <Box fontSize="sm" flex={1} maxWidth="80px">
                {officeHour.isEnabled ? officeHourTerminology.ot : officeHourTerminology.ct}
              </Box>
              <Box fontSize="sm" flex={1} maxWidth="230px">
                {officeHour.isEnabled && !officeHour.isOpenTwentyFourHours && (
                  <>
                    {hasPermission ? (
                      <TimeRange
                        startTimePickerProps={{
                          initialTime: utcToZonedTime(officeHour.openAt, clinicTimezone),
                          onTimeChange: (time: Date): void => {
                            handleTimeChange({ index, field: IClinicHourSectionField.OpenAt, date: time });
                          },
                          inputProps: {
                            rightElement: undefined,
                            size: 'sm',
                          },
                          placement: 'bottom',
                        }}
                        endTimePickerProps={{
                          initialTime: utcToZonedTime(officeHour.closeAt, clinicTimezone),
                          onTimeChange: (time: Date): void => {
                            handleTimeChange({ index, field: IClinicHourSectionField.CloseAt, date: time });
                          },
                          inputProps: {
                            rightElement: undefined,
                            size: 'sm',
                          },
                          placement: 'bottom',
                        }}
                      />
                    ) : (
                      <Text>
                        {format(utcToZonedTime(officeHour.openAt, clinicTimezone), 'h:mm a')} to{' '}
                        {format(utcToZonedTime(officeHour.closeAt, clinicTimezone), 'h:mm a')}
                      </Text>
                    )}
                  </>
                )}
              </Box>
              <Box fontSize="sm" flex={1} maxWidth="200px" ml={8} zIndex={0}>
                {officeHour.isEnabled && (
                  <>
                    {hasPermission ? (
                      <Switch
                        name={`isOpenTwentyFourHours-${index}`}
                        label={`${officeHourTerminology.ot} 24 Hours`}
                        isChecked={officeHour.isOpenTwentyFourHours}
                        onChange={(e): void =>
                          onClinicHourSectionChange(
                            index,
                            IClinicHourSectionField.IsOpenTwentyFourHours,
                            e.currentTarget.checked,
                          )
                        }
                        isFieldInline
                        labelPosition="end"
                        labelStyle={{ fontSize: 'sm' }}
                      />
                    ) : (
                      <Text size="sm">
                        {officeHour.isOpenTwentyFourHours ? `${officeHourTerminology.ot} 24 Hours` : ''}
                      </Text>
                    )}
                  </>
                )}
              </Box>
            </Flex>
          );
        })}
      </Flex>
    );
  },
);

OfficeHoursPicker.displayName = 'OfficeHoursPicker';

export default OfficeHoursPicker;
